import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ActiveBet } from '../active-bet';
import { MoveCards } from '../move-cards';
import { StreamDesktopHistoryMoves } from '../moves-history';
import { PlayerCard } from '../player-card';
import { RoomBox } from '../room-box';
import { RoomDescriptionText } from '../room-description-text';
import { RoundCounter } from '../round-counter';
import { Score } from '../score';

import DesktopLayout from 'shared/components/layout/desktop-layout';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { Spinner } from 'shared/components/ui/Loaders/spinner/spinner';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum, WinnerStatusEnum } from 'shared/constants/shared-enums';
import { CurrencyType } from 'shared/web3';

import floor from 'assets/png/floor.png?format=webp;avif&as=picture';

import { useStreamRoom } from './use-stream-room';
import PixiComponent from '@/pixi/PixiComponent';

export interface IStreamRoomDesktopProps {
  isGuestMode: boolean;
  streamCurrency?: CurrencyType | undefined;
}

export const StreamRoomDesktop = ({ isGuestMode, streamCurrency }: IStreamRoomDesktopProps) => {
  const { t } = useTranslation();

  const {
    isInitialLoading,
    is404,
    playerAWonRound,
    playerBWonRound,
    roundDraw,
    playerASelectedMove,
    playerBSelectedMove,
    playerAWins,
    playerBWins,
    renderEndRoundPhrase,
    currentPhase,
    currentRound,
    queue,
    room,
    roomDescription,
  } = useStreamRoom({ isGuestMode });

  const isGameFinished = queue?.length === 0 && room.roomStatus === RoomStatusEnum.Closed;
  const isRoundFinished = playerAWonRound || playerBWonRound || roundDraw;

  const historyPlayerA = useRef<HTMLDivElement | null>(null);
  const historyPlayerB = useRef<HTMLDivElement | null>(null);
  const scrollSyncLock = useRef(false);

  useEffect(() => {
    const currentEl = historyPlayerA.current;
    if (!currentEl) return;

    const handleScroll = () => {
      if (historyPlayerB.current) {
        historyPlayerB.current.scrollTop = currentEl.scrollTop;
      }
    };

    currentEl.addEventListener('scroll', handleScroll);

    return () => {
      currentEl.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (isInitialLoading) {
    return (
      <DesktopLayout
        title={
          room.roomStatus !== RoomStatusEnum.None && (
            <>
              {room.roomStatus === RoomStatusEnum.Closed
                ? t('modals.watchGame.gameReplay')
                : t('modals.watchGame.liveGame')}
              {room.roomStatus !== RoomStatusEnum.Closed && (
                <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
              )}
            </>
          )
        }
        isGuestMode={isGuestMode}
      >
        <div className="flex h-full w-full flex-grow items-center justify-center">
          <Spinner />
        </div>
      </DesktopLayout>
    );
  }

  if (is404) {
    return (
      <DesktopLayout
        title={
          room.roomStatus !== RoomStatusEnum.None && (
            <>
              {room.roomStatus === RoomStatusEnum.Closed
                ? t('modals.watchGame.gameReplay')
                : t('modals.watchGame.liveGame')}
              {room.roomStatus !== RoomStatusEnum.Closed && (
                <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
              )}
            </>
          )
        }
        isGuestMode={isGuestMode}
      >
        <div className="flex h-full w-full flex-grow items-center justify-center">
          <div className="mb-[96px] text-8xl font-bold text-white">404</div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      title={
        room.roomStatus !== RoomStatusEnum.None && (
          <>
            {room.roomStatus === RoomStatusEnum.Closed
              ? t('modals.watchGame.gameReplay')
              : t('modals.watchGame.liveGame')}
            {room.roomStatus !== RoomStatusEnum.Closed && (
              <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
            )}
          </>
        )
      }
      isGuestMode={isGuestMode}
      className="overflow-hidden"
    >
      <div className="absolute left-0 z-10 mb-[100px] mt-[100px] flex h-[80%] w-full flex-col md:h-[82%]">
        <PixiComponent />
      </div>

      <div className="relative flex h-full max-h-[1080px] max-w-[1920px] flex-row items-stretch justify-between lg:min-h-[800px] lg:w-full">
        <div className="relative w-full">
          <div className="relative flex h-fit max-h-[1080px] max-w-[1920px] animate-appear-with-blur flex-row items-stretch justify-between gap-[12px] rounded-[24px] border border-border bg-background bg-opacity-30 p-[8px] transition sm:p-[20px] md:w-full lg:h-full lg:min-h-[800px] lg:border-[0] lg:bg-[transparent] lg:p-[0] lg:py-4">
            <div className="z-30 order-1 flex h-fit max-w-[342px] animate-appear-with-blur justify-start sm:max-h-[890px] sm:max-w-[630px] lg:w-full lg:min-w-[295px] lg:max-w-[342px]">
              <PlayerCard
                position="order-1"
                name="Player 1"
                wins={playerBWins}
                playerAddress={room.playerA.address}
                isWinner={
                  isGameFinished &&
                  (room.winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                    room.winnerStatus === WinnerStatusEnum.TechnicalWinnerA)
                }
                isLooser={
                  isGameFinished &&
                  (room.winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                    room.winnerStatus === WinnerStatusEnum.TechnicalWinnerB)
                }
                winsTo={room.winsTo}
              />
            </div>

            <div className="z-40 order-2 flex max-w-[165px] animate-appear-with-blur text-white lg:w-full">
              <StreamDesktopHistoryMoves
                currentRound={currentRound}
                forPlayer="playerA"
                selfRef={historyPlayerB}
                syncRef={historyPlayerA}
                syncLockRef={scrollSyncLock}
                isGameFinished={isGameFinished}
              />
            </div>

            <div className="relative z-30 order-3 flex h-fit w-full max-w-[389px] animate-appear flex-col lg:min-w-[348px] lg:max-w-[438px]">
              <Score playerAWins={playerAWins} playerBWins={playerBWins} />

              <RoundCounter currentRound={currentRound} />

              <AnimatedTransition show={currentPhase === 'done' || isGameFinished}>
                <>{renderEndRoundPhrase()}</>
              </AnimatedTransition>
            </div>

            <div className="z-40 order-4 flex max-w-[165px] animate-appear-with-blur text-white lg:w-full">
              <StreamDesktopHistoryMoves
                currentRound={currentRound}
                forPlayer="playerB"
                selfRef={historyPlayerA}
                syncRef={historyPlayerB}
                syncLockRef={scrollSyncLock}
                isGameFinished={isGameFinished}
              />
            </div>

            <div className="z-30 order-5 flex h-fit max-w-[342px] animate-appear-with-blur justify-start sm:max-h-[890px] sm:max-w-[630px] lg:w-full lg:min-w-[295px] lg:max-w-[342px]">
              {room.playerB.address !== NULL_ADDRESS ? (
                <PlayerCard
                  position="order-3"
                  name="Player 2"
                  wins={playerAWins}
                  playerAddress={room.playerB.address}
                  isWinner={
                    isGameFinished &&
                    (room.winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                      room.winnerStatus === WinnerStatusEnum.TechnicalWinnerB)
                  }
                  isLooser={
                    isGameFinished &&
                    (room.winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                      room.winnerStatus === WinnerStatusEnum.TechnicalWinnerA)
                  }
                  winsTo={room.winsTo}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={`absolute left-[40px] top-[134px] z-50 h-[50px] w-[50px] transition-all duration-500 ease-in-out ${isRoundFinished ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-4 opacity-0'} sm:left-[120px] sm:top-[400px] lg:left-[300px] lg:top-[340px]`}
        >
          <MoveCards
            position="left"
            isRoundFinished={isRoundFinished}
            selectedMove={playerASelectedMove}
            isWinner={playerAWonRound}
            roundDraw={roundDraw}
            currentPhase={currentPhase}
            isGameFinished={isGameFinished}
          />
        </div>

        <div
          className={`absolute right-[40px] top-[134px] z-50 h-[50px] w-[50px] transition-all duration-500 ease-in-out ${isRoundFinished ? 'translate-x-0 opacity-100' : 'pointer-events-none translate-x-4 opacity-0'} sm:right-[160px] sm:top-[400px] lg:right-[330px] lg:top-[340px]`}
        >
          <MoveCards
            position="right"
            isRoundFinished={isRoundFinished}
            selectedMove={playerBSelectedMove}
            isWinner={playerBWonRound}
            roundDraw={roundDraw}
            currentPhase={currentPhase}
            isGameFinished={isGameFinished}
          />
        </div>
      </div>

      {room.bet ? (
        <RoomBox
          token={room.token}
          bet={room.bet ? room.bet * 2 : null}
          animationsTriggered={true}
          streamCurrency={isGuestMode ? streamCurrency : undefined}
        />
      ) : null}

      <ActiveBet
        bet={room.bet}
        token={room.token}
        animationsTriggered={true}
        streamCurrency={isGuestMode ? streamCurrency : undefined}
      />

      <OptimizedImage
        imageData={floor}
        alt="floor"
        className="absolute bottom-[110px] left-0 right-0 z-0 h-[20%] w-full lg:bottom-[0]"
      />

      <RoomDescriptionText roomDescription={roomDescription} />
    </DesktopLayout>
  );
};
